import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, MapPin, Building2 } from "lucide-react";

// Assets
import Makkah from "../assets/Projects/Makkah/Makkah.png";
import Neom1 from "../assets/Projects/Neom/Neom1.png";
import Doha from "../assets/Projects/doha/Doha.png";
import KSA from "../assets/Projects/KSA_Project/KSA.png";
import USA from "../assets/Projects/UsaProject/USA.png";
import Kuwait from "../assets/Projects/Kuwait/Kuwait.png";
import USA2Project from "../assets/Projects/UsaProject2/USA-Project-1.png";
import USA3Project from "../assets/Projects/UsaProject3/USA-Project-3.png";

const projects = [
  {
    id: 1,
    title: "Makkah & Medina Saudi Arabia",
    category: "URBAN EXPANSION",
    description: "Third Saudi Expansion of Holy Haram, Makkah (Project Nr: 2020/5454)",
    images: [Makkah],
  },
  {
    id: 2,
    title: "Neom Projects Saudi Arabia",
    category: "SMART CITIES",
    description: "Sooners Land has Prepared Bidding Package for Contractors. Neom, KSA",
    images: [Neom1],
  },
  {
    id: 3,
    title: "Zekreet, Qatar",
    category: "DEFENSE INFRASTRUCTURE",
    description: "Doha Naval Base Project",
    images: [Doha],
  },
  {
    id: 4,
    title: "Jizzan to Empty Quarter Saudi Arabia",
    category: "ROADS & HIGHWAYS",
    description: "South Border Infrastructure Project (1600 Km road Construction and Supervision)",
    images: [KSA],
  },
  {
    id: 5,
    title: "Red River Bridge State of Oklahoma/Texas, USA",
    category: "BRIDGE ENGINEERING",
    description: "Infrastructure Federal Highway Project Management.",
    images: [USA],
  },
  {
    id: 6,
    title: "George Bush Intercontinental Airport Houston, TX, USA",
    category: "AVIATION INFRASTRUCTURE",
    description: "Materials Testing and Project Quality Control.",
    images: [USA3Project],
  },
  {
    id: 7,
    title: "State of Tennessee USA",
    category: "ENVIRONMENTAL RECLAMATION",
    description: "Surface Mining Reclamation Projects.",
    images: [USA2Project],
  },
  {
    id: 8,
    title: "Al-Jahra, Kuwait",
    category: "RESIDENTIAL DEVELOPMENT",
    description: "Large Scale Housing Projects.",
    images: [Kuwait],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const Projects = () => {
  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Our Projects | Sooners Land LLC Engineering</title>
        <meta name="description" content="Explore our global engineering portfolio, featuring projects in Saudi Arabia, USA, Qatar, and Kuwait." />
      </Helmet>

      {/* Header */}
      <section className="bg-primary pt-32 pb-20 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/10 -skew-x-12 translate-x-1/2" />
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Pioneering Global <br />
            <span className="text-accent italic">Infrastructure.</span>
          </motion.h1>
          <p className="text-xl text-slate-400">
            Showcasing our commitment to engineering excellence across diverse sectors and continents.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding -mt-10">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="group relative"
              >
                <Link to={`/projects/${project.id}`} className="block">
                  <div className="relative h-[450px] rounded-[2rem] overflow-hidden shadow-2xl transition-all duration-500 group-hover:shadow-accent/20 group-hover:-translate-y-2">
                    {/* Project Image */}
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent group-hover:via-primary/50 transition-all duration-500" />
                    
                    {/* Project Info Overlay */}
                    <div className="absolute bottom-0 left-0 p-10 w-full">
                      <div className="mb-4">
                        <span className="px-3 py-1 bg-accent/20 backdrop-blur-md border border-accent/30 text-accent font-bold text-[10px] uppercase tracking-widest rounded-full">
                          {project.category}
                        </span>
                      </div>
                      <h2 className="text-3xl font-extrabold text-white mb-4 leading-tight group-hover:text-accent transition-colors duration-300">
                        {project.title}
                      </h2>
                      <div className="flex items-center gap-6 text-slate-300 text-sm font-medium mb-6">
                        <div className="flex items-center gap-2">
                           <MapPin size={16} className="text-accent" />
                           {project.title.split(',').pop()?.trim()}
                        </div>
                        <div className="flex items-center gap-2">
                           <Building2 size={16} className="text-accent" />
                           Infrastructure
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-white font-bold group/btn">
                         <span>View Case Study</span>
                         <ArrowUpRight size={20} className="transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="section-padding bg-slate-100">
        <div className="container mx-auto text-center bg-white p-16 rounded-[3rem] shadow-xl border border-slate-200">
          <h3 className="text-3xl font-bold mb-6">Have a project in mind?</h3>
          <p className="text-slate-500 text-lg mb-10 max-w-2xl mx-auto">
            Let's work together to build world-class infrastructure that lasts. Our team or experts is ready to discuss your requirements.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary px-10 py-4 inline-flex shadow-2xl hover:shadow-accent/40"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Projects;
